import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

function fileUrl(record, fileField, thumb) {
  try {
    if (!record || !record[fileField]) return '';
    return pb.files.getURL(record, record[fileField], thumb ? { thumb } : undefined);
  } catch {
    return '';
  }
}

const emptyForm = {
  name: '',
  role: '',
  tagline: '',
  bio_short: '',
  bio: '',
  linkedin_url: '',
  website_url: '',
  email: '',
  sort_order: 0,
  is_active: true,
  photo: null,
};

function AdminSiteTeamPage() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const [photoDragOver, setPhotoDragOver] = React.useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = React.useState('');
  const [removeExistingPhoto, setRemoveExistingPhoto] = React.useState(false);
  const photoInputRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      try {
        if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
      } catch { }
    };
  }, [photoPreviewUrl]);

  const fetchItems = async () => {
    setError('');
    try {
      const res = await pb.collection('site_team_members').getList(1, 200, { sort: 'sort_order, -created' });
      setItems(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement équipe');
      setItems([]);
    }
  };

  React.useEffect(() => {
    fetchItems();
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_team_members').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setItems((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...items];
    await persistOrder(ordered);
    await fetchItems();
  };

  const openCreate = () => {
    setEditing(null);
    setPhotoDragOver(false);
    setRemoveExistingPhoto(false);
    try {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    } catch { }
    setPhotoPreviewUrl('');
    setForm({ ...emptyForm, sort_order: items.length ? Math.max(...items.map(i => Number(i.sort_order || 0))) + 1 : 0 });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setPhotoDragOver(false);
    setRemoveExistingPhoto(false);
    try {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    } catch { }
    setPhotoPreviewUrl('');
    setForm({
      name: it?.name || '',
      role: it?.role || '',
      tagline: it?.tagline || '',
      bio_short: it?.bio_short || '',
      bio: it?.bio || '',
      linkedin_url: it?.linkedin_url || '',
      website_url: it?.website_url || '',
      email: it?.email || '',
      sort_order: Number(it?.sort_order || 0),
      is_active: it?.is_active !== false,
      photo: null,
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);

    setPhotoDragOver(false);
    setRemoveExistingPhoto(false);
    try {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    } catch { }
    setPhotoPreviewUrl('');
  };

  const setPhotoFile = (file) => {
    if (!(file instanceof File)) return;
    setRemoveExistingPhoto(false);
    setForm((f) => ({ ...f, photo: file }));
    try {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    } catch { }
    try {
      const url = URL.createObjectURL(file);
      setPhotoPreviewUrl(url);
    } catch {
      setPhotoPreviewUrl('');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const data = new FormData();
      data.append('name', form.name || '');
      data.append('role', form.role || '');
      data.append('tagline', form.tagline || '');
      data.append('bio_short', form.bio_short || '');
      data.append('bio', form.bio || '');
      data.append('linkedin_url', form.linkedin_url || '');
      data.append('website_url', form.website_url || '');
      data.append('email', form.email || '');
      data.append('sort_order', String(Number(form.sort_order || 0)));
      data.append('is_active', form.is_active ? 'true' : 'false');
      if (removeExistingPhoto) data.append('photo', '');
      if (form.photo instanceof File) data.append('photo', form.photo);

      if (editing?.id) {
        await pb.collection('site_team_members').update(editing.id, data);
      } else {
        await pb.collection('site_team_members').create(data);
      }
      await fetchItems();
      closeModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    try {
      await pb.collection('site_team_members').delete(id);
      await fetchItems();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (it) => {
    try {
      await pb.collection('site_team_members').update(it.id, { is_active: !(it?.is_active !== false) });
      await fetchItems();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <AdminLayout title="">
      <h1 style={{ marginTop: 0 }}>Équipe</h1>
      <p>Gérez les membres affichés sur la page “À propos”.</p>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .toggle-pill{ cursor:pointer; border:1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); padding:6px 10px; border-radius:999px; font-size:.85rem; }
        .toggle-pill.on{ border-color: rgba(34,197,94,0.55); box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
        .toggle-pill.off{ opacity:.8; }
        .grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 12px; }
        @media (max-width: 480px){ .grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 920px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }

        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"], .field input[type="url"], .field textarea{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field textarea{ min-height: 120px; resize: vertical; }
        .field input:focus, .field textarea:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }

        .avatar{ width: 56px; height: 56px; border-radius: 999px; object-fit: cover; border: 1px solid var(--admin-border); }

        .dropzone{
          border: 2px dashed var(--admin-border);
          border-radius: 12px;
          padding: 14px;
          background: var(--admin-panel-2);
          display:flex;
          gap: 12px;
          align-items: center;
          justify-content: space-between;
        }
        .dropzone.dragover{
          border-color: rgba(79,70,229,0.55);
          box-shadow: 0 0 0 3px rgba(79,70,229,0.16);
        }
        .dropzone-left{ display:flex; flex-direction:column; gap:4px; }
        .dropzone-title{ font-weight:700; }
        .dropzone-sub{ color: var(--admin-muted); font-size:.9rem; }
        .dropzone-preview{ width: 80px; height: 80px; border-radius: 999px; object-fit: cover; border:1px solid var(--admin-border); background: rgba(0,0,0,0.06); }
        .dropzone-thumb{ position: relative; width: 80px; height: 80px; }
        .dropzone-thumb .dropzone-preview{ width: 80px; height: 80px; }
        .dropzone-thumb-actions{ position: absolute; right: 6px; top: 6px; display:flex; gap: 6px; }
        .dropzone-thumb-actions button{ cursor:pointer; border:1px solid var(--admin-border); background: rgba(0,0,0,0.45); color:#fff; padding: 6px; border-radius: 10px; }
        .dropzone-thumb-actions button:hover{ background: rgba(0,0,0,0.60); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Ordre d’affichage contrôlé par sort_order.</div>
        <button className="btn btn-solid btn-primary" onClick={openCreate}>Nouveau membre</button>
      </div>

      <div className="grid">
        {items.map((it, idx) => (
          <div
            key={it.id}
            className="card"
            draggable
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e, idx)}
            onDrop={(e) => {
              e.preventDefault();
              onDrop();
            }}
            title={`Glissez pour réordonner (position ${idx + 1})`}
            style={{ cursor: 'grab' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {fileUrl(it, 'photo', '200x200') ? (
                  <img className="avatar" src={fileUrl(it, 'photo', '200x200')} alt="" />
                ) : null}
                <div>
                  <div style={{ fontWeight: 800, lineHeight: 1.2 }}>{it.name || '(Sans nom)'}</div>
                  <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem', marginTop: 4 }}>{it.role || ''}</div>
                  <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem', marginTop: 6 }}>ordre: {idx + 1}</div>
                </div>
              </div>
              <button className={`toggle-pill ${(it?.is_active !== false) ? 'on' : 'off'}`} onClick={() => handleToggleActive(it)}>
                {(it?.is_active !== false) ? 'Actif' : 'Inactif'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn" onClick={() => openEdit(it)}>Modifier</button>
              <button className="btn btn-danger" onClick={() => handleDelete(it.id)}>Supprimer</button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier le membre' : 'Nouveau membre'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label>Nom</label>
                <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="field">
                <label>Rôle</label>
                <input type="text" value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Tagline</label>
                <input type="text" value={form.tagline} onChange={(e) => setForm(f => ({ ...f, tagline: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Bio courte</label>
                <textarea value={form.bio_short} onChange={(e) => setForm(f => ({ ...f, bio_short: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Bio (complète)</label>
                <textarea value={form.bio} onChange={(e) => setForm(f => ({ ...f, bio: e.target.value }))} />
              </div>
              <div className="field">
                <label>LinkedIn URL</label>
                <input type="url" value={form.linkedin_url} onChange={(e) => setForm(f => ({ ...f, linkedin_url: e.target.value }))} />
              </div>
              <div className="field">
                <label>Site web URL</label>
                <input type="url" value={form.website_url} onChange={(e) => setForm(f => ({ ...f, website_url: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input
                  type="number"
                  className="no-spin"
                  value={form.sort_order}
                  onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                />
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              </div>
              <div className="field full">
                <label>Photo</label>
                <div
                  className={`dropzone ${photoDragOver ? 'dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setPhotoDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setPhotoDragOver(true); }}
                  onDragLeave={() => setPhotoDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setPhotoDragOver(false);
                    const file = e.dataTransfer?.files?.[0];
                    if (file && file.type?.startsWith('image/')) setPhotoFile(file);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      photoInputRef.current?.click();
                    }
                  }}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <div className="dropzone-left">
                    <div className="dropzone-title">Glisser-déposer une photo</div>
                    <div className="dropzone-sub">ou cliquer pour choisir un fichier</div>
                    {form.photo instanceof File ? (
                      <div className="dropzone-sub">Sélection: {form.photo.name}</div>
                    ) : null}
                    {removeExistingPhoto ? (
                      <div className="dropzone-sub" style={{ color: '#ef4444' }}>Photo supprimée (en attente d’enregistrement)</div>
                    ) : null}
                  </div>

                  {(photoPreviewUrl || (editing?.id && fileUrl(editing, 'photo', '200x200'))) && !removeExistingPhoto ? (
                    <div className="dropzone-thumb" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <img
                        className="dropzone-preview"
                        src={photoPreviewUrl || fileUrl(editing, 'photo', '200x200')}
                        alt={photoPreviewUrl ? 'Aperçu' : 'Photo actuelle'}
                      />
                      <div className="dropzone-thumb-actions">
                        <button
                          type="button"
                          title="Supprimer la photo"
                          aria-label="Supprimer la photo"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setForm((f) => ({ ...f, photo: null }));
                            try {
                              if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
                            } catch { }
                            setPhotoPreviewUrl('');
                            if (photoInputRef.current) photoInputRef.current.value = '';
                            if (editing?.id) setRemoveExistingPhoto(true);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setPhotoFile(file);
                  }}
                />
                <small>{editing?.id ? 'Laisser vide pour conserver la photo actuelle' : 'Choisir une image'}</small>
              </div>
            </div>
            <div className="modal-actions">
              {editing?.id && (
                <button className="btn btn-danger" onClick={() => { closeModal(); handleDelete(editing.id); }}>Supprimer</button>
              )}
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSave} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )
      }
    </AdminLayout >
  );
}

export default AdminSiteTeamPage;
